import { isInMsTeamsAuthWrapper, useAuth } from "@healthos/auth/react";
import { cn } from "@healthos/ui";
import { SignOut } from "@phosphor-icons/react";
import { NavLink } from "react-router-dom";
import { DEFAULT_NAV_ITEMS, type DefaultNavItem } from "./nav-items";
import { useDarkTheme } from "./use-dark-theme";

export interface BottomNavProps {
  items?: DefaultNavItem[];
  className?: string;
}

export function BottomNav({ items = DEFAULT_NAV_ITEMS, className }: BottomNavProps) {
  const { state, signOut } = useAuth();
  // Toned icons are the dark theme's; light keeps the original teal outline.
  const dark = useDarkTheme();
  const showSignOut = state.status === "signedIn" && !isInMsTeamsAuthWrapper();
  const visibleItems = items.filter((item) => !item.hideOnMobile).slice(0, showSignOut ? 4 : 5);

  return (
    <nav
      data-app-chrome="bottom-nav"
      aria-label="Primary"
      className={cn(
        "md:hidden fixed bottom-0 left-0 right-0 z-40 flex justify-center pointer-events-none",
        className,
      )}
    >
      <div className="w-full max-w-md pointer-events-auto">
        <div
          className="mx-3 mb-3 rounded-xl bg-surface-elevated/90 backdrop-blur-md border border-border-default"
          style={{
            paddingBottom: "env(safe-area-inset-bottom)",
            boxShadow: "0 8px 30px color-mix(in srgb, var(--color-teal-700) 8%, transparent)",
          }}
        >
          <ul className={cn("grid", gridCols(visibleItems.length + (showSignOut ? 1 : 0)))}>
            {visibleItems.map(
              ({
                id,
                label,
                compactLabel,
                path,
                Icon,
                tone,
                end,
                disableViewTransition,
                badge,
              }) => (
                <li key={id}>
                  <NavLink
                    to={path}
                    end={end ?? (path === "/" || path === "/operations")}
                    viewTransition={!disableViewTransition}
                    aria-label={badge && badge > 0 ? `${label}, ${badge} unread` : undefined}
                    className="w-full flex flex-col items-center justify-center gap-0.5 py-2 md:py-3 transition-colors"
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          className={cn(
                            "relative flex items-center justify-center w-9 h-8 rounded-full [transition:background-color_180ms_var(--ease-out-expo),color_180ms_var(--ease-out-expo)]",
                            isActive ? "bg-surface-accent-leaf text-ink-brand" : "text-ink-teal",
                          )}
                        >
                          <Icon
                            size={18}
                            weight={(dark && tone) || isActive ? "fill" : "regular"}
                            color={dark ? tone : undefined}
                          />
                          {badge && badge > 0 ? (
                            <span
                              aria-hidden="true"
                              className="absolute -top-0.5 -right-1 min-w-4 rounded-full bg-teal-700 px-1 text-center font-mono text-2xs font-medium leading-4 tabular-nums text-ink-on-brand ring-2 ring-surface-elevated"
                            >
                              {badge}
                            </span>
                          ) : null}
                        </span>
                        <span
                          className={cn(
                            "text-2xs font-medium tracking-tight leading-tight text-center px-0.5 transition-colors",
                            isActive ? "text-ink-brand" : "text-ink-teal",
                          )}
                        >
                          {compactLabel ?? label}
                        </span>
                      </>
                    )}
                  </NavLink>
                </li>
              ),
            )}
            {showSignOut ? (
              <li>
                <button
                  type="button"
                  onClick={() => signOut()}
                  className="w-full flex flex-col items-center justify-center gap-0.5 py-2 md:py-3 transition-colors"
                >
                  <span className="flex items-center justify-center w-9 h-8 rounded-full text-ink-teal hover:text-ink-brand transition-colors">
                    <SignOut size={18} weight="regular" />
                  </span>
                  <span className="text-2xs font-medium tracking-tight leading-tight text-center px-0.5 text-ink-teal">
                    Sign out
                  </span>
                </button>
              </li>
            ) : null}
          </ul>
        </div>
      </div>
    </nav>
  );
}

function gridCols(n: number): string {
  switch (n) {
    case 2:
      return "grid-cols-2";
    case 3:
      return "grid-cols-3";
    case 4:
      return "grid-cols-4";
    case 5:
      return "grid-cols-5";
    case 6:
      return "grid-cols-6";
    default:
      return "grid-cols-6";
  }
}
